import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { AUTH } from '../../constants/actionTypes.js';
import { STAFF_2FA_PREAUTH_STORAGE_KEY } from '../../constants/twoFALogin.js';
import { apiStaff2FAVerifyLogin } from '../../api/index.js';
import lusukuLogo from 'assets/img/lusukuLogo517.png';
import { resolveLandingRoute } from '../../utilities/landingRoute.js';

function readTokenFromQuery(search) {
    const raw = new URLSearchParams(search).get('token');
    if (!raw) return '';
    try {
        return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch {
        return raw;
    }
}

const StaffTwoFAVerify = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    const [code, setCode] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rememberDevice, setRememberDevice] = useState(false);
    const rememberDeviceRef = useRef(false);
    const [preAuthToken, setPreAuthToken] = useState('');
    const inputRefs = useRef([]);
    const verifyInFlight = useRef(false);

    useEffect(() => {
        const fromStorage =
            typeof sessionStorage !== 'undefined'
                ? sessionStorage.getItem(STAFF_2FA_PREAUTH_STORAGE_KEY) || ''
                : '';
        const fromQuery = readTokenFromQuery(location.search);
        const t = fromStorage || fromQuery;
        if (!t) {
            toast.error('Invalid session. Please log in again.');
            history.push('/auth/login-page');
            return;
        }
        setPreAuthToken(t);
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }, [location.search]);

    const clearPreAuthAndGoLogin = () => {
        try {
            sessionStorage.removeItem(STAFF_2FA_PREAUTH_STORAGE_KEY);
        } catch (_) { /* ignore */ }
        history.push('/auth/login-page');
    };

    const performVerify = async (totpCode) => {
        if (verifyInFlight.current || !preAuthToken || totpCode.length !== 6 || !/^\d{6}$/.test(totpCode)) return;
        verifyInFlight.current = true;
        setError('');
        setLoading(true);
        try {
            const response = await apiStaff2FAVerifyLogin({
                preAuthToken,
                totpCode,
                rememberDevice: rememberDeviceRef.current,
            });
            const { result, token, refreshToken } = response.data;
            try {
                sessionStorage.removeItem(STAFF_2FA_PREAUTH_STORAGE_KEY);
            } catch (_) { /* ignore */ }
            // Keep the refresh token this login issued, under the same key the
            // 401 interceptor in api/index.js reads and rotates. This is the
            // path privileged staff take — every account with 2FA enabled
            // finishes its login here rather than in the signin action — so
            // omitting it would leave exactly the accounts that matter most
            // without silent renewal.
            try {
                if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
            } catch (_) { /* private mode: session just ends at token expiry */ }
            dispatch({ type: AUTH, data: { result, token, refreshToken } });
            toast.success(`Welcome back, ${result.name || result.email}!`);
            history.replace(resolveLandingRoute(result));
        } catch (err) {
            const msg = err.response?.data?.message || 'Invalid code. Please try again.';
            setError(msg);
            if (err.response?.data?.code === 'SESSION_EXPIRED') {
                try {
                    sessionStorage.removeItem(STAFF_2FA_PREAUTH_STORAGE_KEY);
                } catch (_) { /* ignore */ }
                setTimeout(() => history.push('/auth/login-page'), 2000);
            }
            setCode(['', '', '', '', '', '']);
            setTimeout(() => inputRefs.current[0]?.focus(), 50);
        } finally {
            verifyInFlight.current = false;
            setLoading(false);
        }
    };

    const handleDigitChange = (index, value) => {
        if (value.length === 6 && /^\d+$/.test(value)) {
            const digits = value.split('');
            setCode(digits);
            inputRefs.current[5]?.focus();
            queueMicrotask(() => performVerify(value));
            return;
        }
        if (!/^\d?$/.test(value)) return;
        const newCode = [...code];
        newCode[index] = value;
        setCode(newCode);
        if (value && index < 5) inputRefs.current[index + 1]?.focus();
        const joined = newCode.join('');
        if (joined.length === 6 && /^\d{6}$/.test(joined)) {
            queueMicrotask(() => performVerify(joined));
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !code[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const totpCode = code.join('');
        if (totpCode.length !== 6) {
            setError('Please enter all 6 digits.');
            return;
        }
        await performVerify(totpCode);
    };

    const digitBoxStyle = (filled) => ({
        width: 52, height: 60, fontSize: 24, fontWeight: 700, textAlign: 'center',
        borderRadius: 10, border: `2px solid ${filled ? '#006202' : '#D1D5DB'}`,
        outline: 'none', color: '#0a0f2c', background: filled ? '#F0FDF4' : '#fff',
        transition: 'border-color 0.15s, background 0.15s',
        caretColor: 'transparent',
    });

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #011a03 0%, #013d06 55%, #006202 100%)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            padding: 20,
        }}>
            <div style={{
                background: '#fff', borderRadius: 20, padding: '48px 40px',
                maxWidth: 420, width: '100%',
                boxShadow: '0 25px 60px rgba(0,0,0,0.35)', textAlign: 'center',
            }}>
                <div style={{
                    width: 72, height: 72, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #011a03, #006202)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    margin: '0 auto 20px', fontSize: 32,
                }}>
                    🔐
                </div>

                <img src={lusukuLogo} alt="Lusuku" style={{ width: 40, marginBottom: 16 }} />

                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#0a0f2c', margin: '0 0 8px' }}>
                    Two-Factor Authentication
                </h2>
                <p style={{ color: '#6B7280', fontSize: 14, margin: '0 0 32px', lineHeight: 1.5 }}>
                    Open <strong>Google Authenticator</strong> and enter the 6-digit code for your Lusuku Dashboard account.
                    {' '}We verify automatically when all six digits are filled; you can also press <strong>Enter</strong> or tap the button below.
                </p>

                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
                        {code.map((digit, i) => (
                            <input
                                key={i}
                                ref={el => inputRefs.current[i] = el}
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={digit}
                                onChange={e => handleDigitChange(i, e.target.value)}
                                onKeyDown={e => handleKeyDown(i, e)}
                                style={digitBoxStyle(!!digit)}
                                onFocus={e => e.target.style.borderColor = '#006202'}
                                onBlur={e => e.target.style.borderColor = digit ? '#006202' : '#D1D5DB'}
                            />
                        ))}
                    </div>

                    {error && (
                        <div style={{
                            background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
                            padding: '10px 14px', color: '#991B1B', fontSize: 13, marginBottom: 20,
                        }}>
                            {error}
                        </div>
                    )}

                    <label
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 10,
                            textAlign: 'left',
                            fontSize: 13,
                            color: '#374151',
                            marginBottom: 20,
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={rememberDevice}
                            onChange={(e) => {
                                rememberDeviceRef.current = e.target.checked;
                                setRememberDevice(e.target.checked);
                            }}
                            style={{ marginTop: 2, width: 16, height: 16, flexShrink: 0 }}
                        />
                        <span>
                            Remember this device for up to 5 days — after you sign in, use password only on this browser (signing out does not remove this). Only use on trusted computers. To require the authenticator again, use &quot;Forget this device&quot; on the login page.
                        </span>
                    </label>

                    <button
                        type="submit"
                        disabled={loading || code.join('').length !== 6}
                        style={{
                            width: '100%', padding: '14px', borderRadius: 10, border: 'none',
                            background: code.join('').length === 6 && !loading ? '#006202' : '#9CA3AF',
                            color: '#fff', fontSize: 15, fontWeight: 600,
                            cursor: code.join('').length === 6 && !loading ? 'pointer' : 'not-allowed',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                            transition: 'background 0.2s',
                        }}
                    >
                        {loading ? <><Spinner size="sm" animation="border" /> Verifying...</> : 'Verify & Sign In'}
                    </button>
                </form>

                <div style={{ marginTop: 24, padding: '14px', background: '#F9FAFB', borderRadius: 8 }}>
                    <p style={{ fontSize: 12, color: '#6B7280', margin: 0, lineHeight: 1.6 }}>
                        Lost your authenticator? Enter a backup code above instead of a 6-digit TOTP code.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={clearPreAuthAndGoLogin}
                    style={{
                        marginTop: 16, background: 'none', border: 'none',
                        color: '#9CA3AF', fontSize: 13, cursor: 'pointer',
                    }}
                >
                    ← Back to login
                </button>
            </div>
        </div>
    );
};

export default StaffTwoFAVerify;
